
import React, { useState, useEffect } from 'react';
import { CycleData, HormoneType } from '@/utils/cycle-tracking';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, BarChart, Brain } from 'lucide-react';
import { CycleInsights } from '@/components/cycle-tracking/CycleInsights';
import { HormoneChart } from '@/components/cycle-tracking/HormoneChart';
import { useSymptomData } from '@/hooks/useSymptomData';
import { usePeriodHistory } from '@/components/cycle-tracking/history/usePeriodHistory';
import { useUser } from '@/context/UserContext';
import CycleAnalysisCard from '@/components/cycle-tracking/history/CycleAnalysisCard';
import PeriodHistoryCard from '@/components/cycle-tracking/history/PeriodHistoryCard';
import { supabase } from "@/integrations/supabase/client";
import { AdviceDisplay } from '../advice/AdviceDisplay';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface CycleInsightsTabProps {
  cycleData: CycleData | null;
  isLoading: boolean;
}

const CycleInsightsTab: React.FC<CycleInsightsTabProps> = ({ cycleData, isLoading }) => {
  const { user } = useUser();
  const { 
    periodHistory, 
    isLoading: historyLoading, 
    averageCycleLength, 
    averagePeriodLength 
  } = usePeriodHistory(user?.id);
  
  // Hormone selection state
  const [selectedHormones, setSelectedHormones] = React.useState<HormoneType[]>(['estrogen', 'progesterone']);
  
  // AI Advice state
  const [advice, setAdvice] = useState<string>('');
  const [isLoadingAdvice, setIsLoadingAdvice] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  
  // Function to fetch advice from the edge function
  const fetchAdvice = async () => {
    if (!cycleData) {
      return;
    }
    
    setIsLoadingAdvice(true);
    setHasError(false);
    
    try {
      const { data, error } = await supabase.functions.invoke('cycle-advice', {
        body: {
          phase: cycleData.currentPhase,
          day: cycleData.currentDay,
          cycleLength: cycleData.cycleLength,
          interests: ['nutrition', 'exercise', 'mood']
        }
      });
      
      if (error) throw error;
      
      setAdvice(data.advice);
    } catch (error) {
      console.error('Error fetching AI advice:', error);
      setHasError(true);
      toast.error('Failed to get advice. Please try again.');
    } finally {
      setIsLoadingAdvice(false);
    }
  };
  
  // Fetch advice when the component mounts
  useEffect(() => {
    if (cycleData && !advice && !isLoadingAdvice) {
      fetchAdvice();
    }
  }, [cycleData]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-hormony-coral border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* AI Advice Section - Moved to the top */}
      <section>
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-hormony-coral" />
                  Google AI Insights
                </CardTitle>
              </div>
              {cycleData && (
                <Badge variant="outline" className="capitalize">
                  {cycleData.currentPhase} phase
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <AdviceDisplay 
              advice={advice} 
              isLoading={isLoadingAdvice} 
              hasError={hasError} 
              retryFetch={fetchAdvice}
              selectedInterests={['nutrition', 'exercise', 'mood']}
            />
          </CardContent>
        </Card>
      </section>

      {/* Recommendations Section */}
      <section>
        <h2 className="text-xl font-medium mb-4">Recommendations & Insights</h2>
        <CycleInsights cycleData={cycleData} />
      </section>

      {/* Interactive Visualization Section */}
      <section>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-medium">Cycle Data & History</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="hormones" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="hormones" className="flex items-center gap-1">
                  <LineChart size={16} />
                  Hormone Levels
                </TabsTrigger>
                <TabsTrigger value="history" className="flex items-center gap-1">
                  <BarChart size={16} />
                  Cycle History
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="hormones">
                {cycleData && (
                  <div className="h-64">
                    <HormoneChart 
                      cycleData={cycleData} 
                      selectedHormones={selectedHormones} 
                    />
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="history">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Cycle Analysis Card */}
                  <CycleAnalysisCard 
                    isLoading={historyLoading}
                    periodHistory={periodHistory}
                    averageCycleLength={averageCycleLength}
                    averagePeriodLength={averagePeriodLength}
                  />
                  
                  {/* History List Card */}
                  <PeriodHistoryCard 
                    isLoading={historyLoading}
                    periodHistory={periodHistory}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default CycleInsightsTab;
